import {STEP_DIRECTIONS} from '../_constants';
import {StepDirection} from '../_types';

/*
 * Rounds a number to a specified number of decimal places.
 *
 * This function multiplies the number by a factor of 10 raised to the power of
 * the desired precision, rounds it to the nearest integer, and then divides
 * it back down by the same factor. This effectively rounds the number to the
 * specified number of decimal places.
 */
export const roundToPrecision = (value: number, precision: number): number => {
  const factor = Math.pow(10, precision);
  return Math.round(value * factor) / factor;
};

/**
 * Adds two numbers with fixed precision to avoid floating-point inaccuracies.
 *
 * This function helps mitigate issues with JavaScript's floating-point math
 * by rounding the result to a specified number of decimal places.
 *
 * @param {number} a - The first number to add.
 * @param {number} b - The second number to add.
 * @param {number} [precision=10] - The number of decimal places to round the result to.
 * @returns {number} The sum of `a` and `b`, rounded to the given precision.
 *
 * @example
 * addWithPrecision(0.1, 0.2); // 0.3
 * addWithPrecision(0.1, 0.2, 5); // 0.3
 */
export const addWithPrecision = (
  value: number,
  steppedAdjustment: number,
  precision: number
) => {
  const delta = value + steppedAdjustment;
  const rounded = roundToPrecision(delta, precision);
  return rounded;
};

/**
 * Clamps a number within the inclusive range defined by `min` and `max`.
 *
 * Ensures that `value` does not fall below `min` or exceed `max`.
 * If `value` is less than `min`, returns `min`.
 * If `value` is greater than `max`, returns `max`.
 * Otherwise, returns `value`.
 *
 * @param {number} value - The number to clamp.
 * @param {number} min - The lower bound of the range.
 * @param {number} max - The upper bound of the range.
 * @returns {number} The clamped value.
 *
 * @example
 * clampToRange(5, 1, 10);  // 5
 * clampToRange(-3, 0, 8);  // 0
 * clampToRange(15, 0, 10); // 10
 */
export function clampToRange(value: number, min: number, max: number): number {
  const upperBound = Math.min(value, max);
  return Math.max(upperBound, min);
}

/**
 * Creates a curried function that steps a numeric string value up or down,
 * applying precision-safe arithmetic and clamping to a specified range.
 *
 * @param {number} min - The minimum allowable value.
 * @param {number} max - The maximum allowable value.
 * @param {number} step - The amount to increment or decrement the value by.
 *
 * @returns {(value: string, direction: 'up' | 'down') => string}
 * A function that takes a string input value and a direction ('up' or 'down'),
 * returns the new clamped string value after applying the step.
 *
 * @example
 * const stepValue = applySteppedValue(0, 100, 5);
 * stepValue("42", "up"); // "47"
 * stepValue("3", "down"); // "0"
 */
export const applySteppedValue =
  (min: number, max: number, step: number, precision: number) =>
  (value: string, direction: StepDirection) => {
    // Parse the input value as a float
    const parsed = parseNumber(value);

    // If the value is not a valid number, return the minimum value
    if (isNaN(parsed)) {
      return String(min);
    }

    // Determine the step adjustment based on the direction
    const stepAdjustment = direction === STEP_DIRECTIONS.UP ? step : -step;

    // Add the step adjustment to the parsed value with precision
    const newValue = addWithPrecision(parsed, stepAdjustment, precision);

    // Clamp the new value to the specified min and max range
    const clamped = clampToRange(newValue, min, max);

    // Return the clamped value as a string
    return String(clamped);
  };

/**
 * Checks if a value is out of the specified bounds (min and max).
 *
 * @param {number} min - The minimum allowable value.
 * @param {number} max - The maximum allowable value.
 * @param {number} value - The value to check.
 *
 * @returns {boolean} - Returns true if the value is out of bounds, false otherwise.
 */
export const getIsOutOfBounds =
  (min: number | undefined, max: number | undefined) =>
  (value: number | undefined) => {
    if (min === undefined || max === undefined || value === undefined) {
      return false; // If min or max is undefined, we cannot determine bounds
    } else {
      return value < min || value > max;
    }
  };

/**
 * Returns a function that clamps a given number within the specified minimum and maximum bounds.
 *
 * @param min - The minimum allowed value.
 * @param max - The maximum allowed value.
 * @returns A function that takes a number and returns it clamped between `min` and `max`.
 *
 * @example
 * const clamp = setInBoundsValue(0, 10);
 * clamp(5); // returns 5
 * clamp(-2); // returns 0
 * clamp(15); // returns 10
 */
export const setInBoundsValue =
  (min: number, max: number) => (value: number | undefined) => {
    if (value === undefined) return value;
    if (value < min) return min;
    if (value > max) return max;
    return value;
  };

export function parseNumber(str: number | string) {
  str = String(str);

  return /^-?[.,\d]+$/.test(str) ? Number(str.replace(/,/g, '.')) : NaN;
}
/**
 * Evaluates a simple mathematical expression containing only numbers and MDAS operations.
 * Supports: multiplication (*), division (/), addition (+), subtraction (-), and parentheses.
 * Follows proper order of operations (MDAS).
 *
 * @param expression - The mathematical expression string (e.g., "30*2-1", "10+5*2")
 * @returns The evaluated result as a number, or NaN if the expression is invalid
 *
 * @example
 * evaluateMathExpression("30*2-1") // returns 59
 * evaluateMathExpression("10+5*2") // returns 20
 * evaluateMathExpression("(10+5)*2") // returns 30
 * evaluateMathExpression("3,5*2") // returns 7 (supports comma decimal separator)
 */
export function evaluateMathExpression(expression: string): number {
  if (!expression || typeof expression !== 'string') {
    return NaN;
  }

  // Normalize the expression: remove spaces and replace commas with periods
  const normalized = expression.replace(/\s/g, '').replace(/,/g, '.');

  // Validate that the expression only contains allowed characters
  if (!/^[0-9+\-*/.()]+$/.test(normalized)) {
    return NaN;
  }

  try {
    // Use Function constructor to safely evaluate the expression
    // This is safer than eval() as it doesn't have access to the local scope
    // eslint-disable-next-line no-new-func
    const result = new Function('return ' + normalized)();

    // Ensure the result is a valid number (including Infinity)
    if (typeof result === 'number' && !isNaN(result)) {
      return result;
    }

    return NaN;
  } catch (error) {
    // If evaluation fails, return NaN
    return NaN;
  }
}
